import { useState, useCallback, useMemo } from 'react'
import { type FeatureHighlight } from './types'
import { Flipper } from 'legacy/contexts/flipper/FlipperContext'
import useAccessControl from 'legacy/components/AccessControl/useAccessControl'
import FeatureHighlightManageYourStorefront from 'legacy/components/FeatureHighlights/FeatureHighlightManageYourStorefront'
import FeatureHighlightMerchandiseYourProducts from 'legacy/components/FeatureHighlights/FeatureHighlightMerchandiseYourProducts'
import FeatureHighlightManageYourStorefrontImage from 'assets/featureHighlights/feature1.png'
import FeatureHighlightMerchandiseYourProductsImage from 'assets/featureHighlights/feature2.png'

export interface FeatureEducationContext {
  isOpen: boolean
  onOpen: () => void
  onClose: (id?: unknown) => void
  onDismissAll: () => void
  features: FeatureHighlight[]
  setLastSeenFeatureId: (id?: string) => void
}

const noop = () => {}

export const defaultValues: FeatureEducationContext = {
  isOpen: false,
  onOpen: noop,
  onClose: noop,
  onDismissAll: noop,
  features: [],
  setLastSeenFeatureId: noop,
}

// Where new features should go, example components are currently commented out below
// ID needs to be manually added
// TODO: fill up these features for upcoming launch
const CURRENT_FEATURES = [
  // {
  //   id: 'sfp',
  //   img: FeatureHighlightManageYourStorefrontImage,
  //   name: 'Manage your storefront',
  //   Component: FeatureHighlightManageYourStorefront,
  // },
  // {
  //   id: 'merchandise',
  //   img: FeatureHighlightMerchandiseYourProductsImage,
  //   name: 'Merchandise your products',
  //   Component: FeatureHighlightMerchandiseYourProducts,
  // },
]

const getUnseenFeatures = (featureId: string): { features: FeatureHighlight[]; idx: number } => {
  const idx = CURRENT_FEATURES.findIndex(({ id }) => id === featureId) + 1

  /* Take the most recent 5, we don't want to overload the user */
  if (CURRENT_FEATURES.length - idx > 5) {
    return {
      features: CURRENT_FEATURES.slice(CURRENT_FEATURES.length - 5),
      idx,
    }
  }

  return {
    features: CURRENT_FEATURES.slice(idx),
    idx,
  }
}

const useFeatureEducation = () => {
  const hasAccess = useAccessControl()
  const isFeatureEnabled = hasAccess({
    warehouseFlippers: [Flipper.FeatureEducation],
  })

  const [featureId, setFeatureId] = useState(localStorage.getItem('lastSeenFeatureId'))
  const { idx, features } = useMemo(() => getUnseenFeatures(featureId), [featureId])

  /* 
    If the user has seen none of the new features, assume the user has not seen the modal and display it.
    Otherwise, we assume the user has dismissed the modal and we should not display the modal
    */
  const [isOpen, setIsOpen] = useState(idx === 0 && features.length > 0 ? true : false)

  const setLastSeenFeatureId = useCallback(id => {
    localStorage.setItem('lastSeenFeatureId', id)
  }, [])

  const onClose = useCallback(
    id => {
      setIsOpen(false)
      if (typeof id === 'string') {
        setLastSeenFeatureId(id)
        setFeatureId(id)
      } else {
        setFeatureId(localStorage.getItem('lastSeenFeatureId'))
      }
    },
    [setLastSeenFeatureId]
  )

  const onOpen = useCallback(() => {
    setIsOpen(true)
  }, [])

  const onDismissAll = useCallback(() => {
    const lastFeature = features[features.length - 1]
    setLastSeenFeatureId(lastFeature.id)
    setFeatureId(lastFeature.id)
  }, [features, setLastSeenFeatureId])

  /* hooks cannot be conditional, so we handle the disabled logic at the end */
  if (!isFeatureEnabled) {
    return defaultValues
  }

  return {
    isOpen,
    features,
    onOpen,
    onClose,
    onDismissAll,
    setLastSeenFeatureId,
  }
}

export default useFeatureEducation
